"use client"

import type {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  CSSProperties,
  Ref,
} from "react"
import { useEffect, useMemo, useRef } from "react"
import cn from "classnames"

import { mergeRefs } from "~/functions/mergeRefs"
import { useIsomorphicLayoutEffect } from "~/hooks/useIsomorphicLayoutEffect"
import { useOnScreen } from "~/hooks/useOnScreen"
import type { Video as VideoType } from "~/types/asset"

import type { SharedAssetProps } from "./Asset"
import s from "./Asset.module.scss"

export type VideoProps = {
  ref?: Ref<HTMLSpanElement>
  video: VideoType
  videoProps?: Omit<
    ComponentPropsWithRef<"video">,
    "src" | "preload" | "onLoad"
  >

  /** @default undefined */
  isVideoPlaying?: boolean
  onLoad?: (video: HTMLVideoElement) => void
} & Omit<ComponentPropsWithoutRef<"span">, "onLoad"> &
  SharedAssetProps

export const Video = ({
  ref,
  video,
  videoProps,
  isVideoPlaying,
  onLoad: propOnLoad,
  hasPriority = false,
  loading = "lazy",
  objectFit = "cover",
  animateOnLoad: animateOnLoadProp,
  lazyLoadBackgroundColor,
  ...rest
}: VideoProps) => {
  const containerRef = useRef<HTMLSpanElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)

  const onScreen = useOnScreen(containerRef, {
    isEnabled: loading === "lazy",
    rootMargin: "100px",
  })

  const animateOnLoad = useMemo(() => {
    return hasPriority && animateOnLoadProp === undefined
      ? false
      : (animateOnLoadProp ?? true)
  }, [hasPriority, animateOnLoadProp])

  const mergedContainerRefs = useMemo(() => {
    return mergeRefs(containerRef, ref)
  }, [containerRef, ref])

  const videoPropsWithMergedRefs: ComponentPropsWithRef<"video"> =
    useMemo(() => {
      if (!videoProps) return { ref: videoRef }
      return { ...videoProps, ref: mergeRefs(videoProps.ref, videoRef) }
    }, [videoProps, videoRef])

  useIsomorphicLayoutEffect(() => {
    const container = containerRef.current
    const video = videoRef.current
    if (!container || !video) {
      console.warn("[Video] Container or video ref is missing.")
      return
    }

    const abortController = new AbortController()
    const { signal } = abortController

    const loaded = () => {
      if (propOnLoad) propOnLoad(video)
      container.setAttribute("data-is-loaded", "true")
    }

    // If video already is ready to play, call handler early. This can happen
    // before event listener is even attached. So by checking this first, we
    // can avoid issues with video never calling 'loadedmetadata' event
    if (video.readyState > 0) {
      loaded()
      return
    }

    // Or, video is still loading, so wait for it to be ready
    video.addEventListener("loadedmetadata", loaded, { signal })
    return () => abortController.abort()
  }, [propOnLoad])

  useEffect(() => {
    if (
      videoRef.current &&
      isVideoPlaying !== undefined &&
      // Avoid calling play/pause if video is not yet loaded (avoid errors)
      videoRef.current.readyState > 0
    ) {
      if (isVideoPlaying) videoRef.current.play()
      else videoRef.current.pause()
    }
  }, [isVideoPlaying])

  // When lazy-loaded src is set dynamically, native autoPlay doesn't reliably
  // trigger on an already-mounted element — call play() explicitly.
  useEffect(() => {
    const video = videoRef.current
    if (!video || !onScreen || isVideoPlaying !== undefined) return
    video.play().catch(() => {})
  }, [onScreen, isVideoPlaying])

  return (
    <span
      {...rest}
      ref={mergedContainerRefs}
      className={cn(s.assetContainer, rest.className)}
      style={
        {
          "--lazy-load-background-color": lazyLoadBackgroundColor,
          ...rest.style,
        } as CSSProperties
      }
      data-is-video
      data-has-lazy-load-background-color={
        lazyLoadBackgroundColor !== false && animateOnLoad
      }
      {...(animateOnLoad && { "data-animate-on-load": true })}
    >
      <video
        loop
        autoPlay={isVideoPlaying || isVideoPlaying === undefined}
        muted
        playsInline
        controls={false}
        controlsList="nodownload noplaybackrate"
        disablePictureInPicture
        tabIndex={-1}
        draggable="false"
        // Here video props can only override the props above but not the
        // things after - this is deliberate
        {...videoPropsWithMergedRefs}
        src={onScreen || loading === "eager" ? video.url : undefined}
        className={cn(s.asset, videoPropsWithMergedRefs.className)}
        style={{ objectFit, ...videoPropsWithMergedRefs.style }}
        preload={hasPriority ? "auto" : "metadata"}
      />
    </span>
  )
}
